import React, { useMemo, useState } from "react";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";

import {
  PageHeader,
  PageRoot,
  TypeaheadSelect,
  Select,
  Icon
} from "../../components";
import routes from "../../constants/routes";
import RxTable, {
  Table,
  IColumn,
  TableActionTypes,
  TableDetailCard,
  SearchBar,
  CustomWidget,
  FilterBar
} from "../../components/RxTable";

import formatDate from "../../utils/formatDate";
import downloadFile from "../../utils/downloadFile";
import { useGetManual, useFetch } from "../../utils/useApi";
import { AuditStatusReport, DHB } from "../../constants/interfaces";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    padding: "2em"
  },
  toolBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    "&>div": {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
      flexWrap: "wrap"
    }
  },
  dhb: {
    marginRight: "1em"
  },
  auditYear: {
    width: "50%",
    boxSizing: "border-box",
    paddingRight: 5
  },
  select: {
    marginBottom: theme.spacing(1),
    width: "50%"
  },
  fullWidthSelect: {
    marginBottom: theme.spacing(1),
    width: "100%"
  },
  customFilterWrapper: {
    display: "inline-flex",
    flexWrap: "wrap",
    "&>div": {
      marginRight: "1em"
    }
  },
  widget: {
    width: 250,
    "&>.MuiInput-root": {
      width: "calc(100% - 8px)"
    }
  }
}));

type Props = RouteComponentProps & {
  classes?: any;
  auditStatusMapper: {
    [Key: string]: string;
  };
};

const AuditStatusReportComponent: React.FC<Props> = props => {
  const classes = useStyles();
  const [selectedDhb, setDhb] = useState("");

  const columns = useMemo(() => {
    const columns: IColumn[] = [
      {
        id: "plannedAuditYear",
        label: "Audit Year",
        hide: true
      },
      {
        id: "legalEntityName",
        label: "Legal Entity",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="legalEntityName"
            key="legalEntityName"
            placeholder="Legal entity"
            label="Legal Entity"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "premisesName",
        label: "Premises",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <TypeaheadSelect
            className={classes.fullWidthSelect}
            id="premisesName"
            key="premisesName"
            placeholder="Premises"
            label="Premises"
            onChange={e =>
              onFilterChange({
                value: e ? e.value : "",
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            suggestions={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },

      {
        id: "auditType",
        label: "Audit Type",
        searchable: true
      },

      {
        id: "auditSubType",
        label: "Audit Sub Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditSubType"
            key="auditSubType"
            label="Audit Sub Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditToolType",
        label: "Audit Tool Type",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditToolType"
            key="auditToolType"
            label="Audit Tool Type"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "auditDate",
        label: "Audit Date",
        type: "date",
        searchable: true,
        parse: formatDate.condensed,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="auditDate"
            key="auditDate"
            label="Audit Date"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  fValue === cellValue
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        )
      },
      {
        id: "status",
        label: "Status",
        searchable: true,
        filterWidget: (filterValue, onFilterChange, meta) => (
          <Select
            className={classes.select}
            id="status"
            key="status"
            label="Status"
            onChange={e =>
              onFilterChange({
                value: e.target.value,
                compare: (fValue: string, cellValue: string) =>
                  props.auditStatusMapper[fValue] ===
                  props.auditStatusMapper[cellValue]
              })
            }
            options={meta.suggestions}
            value={filterValue || ""}
          />
        ),
        parse: status => props.auditStatusMapper[status]
      },
      {
        id: "auditReport",
        type: "empty",
        label: "Audit Report",
        meta: {
          hideInDetail: true
        },
        renderCell: (cell, row) => (
          <Icon
            color="primary"
            name="get_app"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              const token = window.localStorage.getItem("token");

              if (!token) throw new Error("Invalid Token");
              if (!row.caseId) throw new Error("Invalid caseId");

              const url = `/mcols/api/audits/downloadAuditReport?caseId=${row.caseId}&token=${token}`;
              downloadFile({ url });
            }}
          />
        )
      },
      {
        id: "camsLink",
        label: "Corrective Action Status",
        type: "empty",
        meta: {
          hideInDetail: true
        },
        renderCell: (data, row) => (
          <Icon
            color="primary"
            name="reply"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              props.history.push(
                `${routes.CAM_STATUS_REPORT.url}${row.caseId}`
              );
            }}
          />
        )
      }
    ];
    return columns;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: dhbList } = useFetch<DHB[]>("/audits/getUserDHBs");

  const {
    data: auditStatusReports,
    query,
    loading: reportLoading
  } = useGetManual<AuditStatusReport[] | null>([]);

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
        label={routes.AUDIT_STATUS_REPORT.label}
      />

      <RxTable>
        <div className={classes.toolBar}>
          <div style={{ flex: 1 }}>
            <Select
              id="dhb"
              className={classes.dhb}
              onChange={e => {
                setDhb(e.target.value);
                query(`/audits/getDHBAuditStatusReport/${e.target.value}`);
              }}
              label="DHB"
              value={selectedDhb}
              options={(dhbList || []).map(({ dhbId, dhbName }) => ({
                value: dhbId,
                label: dhbName
              }))}
            />

            <CustomWidget
              compare={(next, prev) =>
                next.rawData === prev.rawData && next.filters === prev.filters
              }
            >
              {({ dispatch, filters, columns, rawData }) => {
                const auditTypeFilterValue = filters.find(
                  filter => filter.id === "auditType"
                );
                const plannedAuditYearFilterValue = filters.find(
                  filter => filter.id === "plannedAuditYear"
                );

                const filterWidget = ["auditType", "plannedAuditYear"];
                const parsedTableData = rawData.map(row => {
                  return filterWidget.reduce(
                    (option, key) => {
                      option[key] = {
                        label: row[key],
                        value: row[key]
                      };
                      return option;
                    },
                    {} as any
                  );
                });

                const filterOptions = filterWidget.reduce(
                  (result, key) => {
                    result[key] = _.uniqBy(
                      parsedTableData.map(row => row[key]),
                      "label"
                    );

                    return result;
                  },
                  {} as any
                );

                return (
                  <div className={classes.customFilterWrapper}>
                    <Select
                      className={classes.widget}
                      id="auditType"
                      label="Audit Type"
                      disabled={!selectedDhb}
                      multiple
                      onChange={e => {
                        dispatch({
                          name: "auditType",
                          type: TableActionTypes.onFilter,
                          payload: {
                            value: e.target.value,
                            compare: (fValue: string[], cellValue: string) =>
                              fValue.includes(cellValue)
                          }
                        });
                        dispatch({
                          type: TableActionTypes.execFilter
                        });
                      }}
                      options={filterOptions.auditType}
                      renderValue={(selected: string[]) => selected.join(", ")}
                      value={
                        auditTypeFilterValue ? auditTypeFilterValue.value : []
                      }
                    />

                    <Select
                      className={classes.widget}
                      id="plannedAuditYear"
                      label="Planned Audit Year"
                      multiple
                      disabled={!selectedDhb}
                      onChange={e => {
                        dispatch({
                          name: "plannedAuditYear",
                          type: TableActionTypes.onFilter,
                          payload: {
                            value: e.target.value,
                            compare: (fValue: string[], cellValue: string) =>
                              fValue.includes(cellValue)
                          }
                        });
                        dispatch({
                          type: TableActionTypes.execFilter
                        });
                      }}
                      options={filterOptions.plannedAuditYear}
                      renderValue={(selected: string[]) => selected.join(", ")}
                      value={
                        plannedAuditYearFilterValue
                          ? plannedAuditYearFilterValue.value
                          : []
                      }
                    />
                  </div>
                );
              }}
            </CustomWidget>
          </div>

          <div>
            <SearchBar />
            <CustomWidget
              compare={(next, prev) =>
                next.rawData === prev.rawData && next.filters === prev.filters
              }
            >
              {({ dispatch, filters, columns, rawData }) => (
                <FilterBar
                  filters={filters}
                  dispatch={dispatch}
                  data={rawData}
                  columns={columns}
                />
              )}
            </CustomWidget>
          </div>
        </div>

        <Table
          columns={columns}
          loading={reportLoading}
          sorting={{ sortDirection: "desc", id: "auditDate" }}
          data={auditStatusReports || []}
          rowsPerPage={25}
          getRowId={row => row.caseId}
        />

        <TableDetailCard title={row => row.premisesName} columns={columns} />
      </RxTable>
    </PageRoot>
  );
};

const mapStateToProps = ({ audit }: any) => {
  return {
    auditStatusMapper: audit.auditStatusMapper,
    error: audit.error,
    loading: audit.loading
  };
};
export { AuditStatusReportComponent };
export default connect(mapStateToProps)(AuditStatusReportComponent);
